import { memo, useCallback, useMemo } from 'react';
import type { SavedView, SavedViewModelType } from 'src/api/generated';
import MenuDropdown from 'src/modules/shared/components/molecules/dropdowns/menu-dropdown';
import type { DropdownItem } from 'src/modules/shared/components/molecules/dropdowns/dropdown';
import type { ComponentSize } from 'src/modules/shared/types/component.type';
import {
  PiPencilSimple,
  PiUsers,
  PiPlus,
  PiXLight,
  PiTrash,
} from 'react-icons/pi';
import { useSavedViews } from '../../../../hooks/api/saved-views';
import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';
import Loading from 'src/modules/shared/components/atoms/loading';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { Typography } from '@material-tailwind/react';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

interface SavedViewDropdownProps {
  model: SavedViewModelType;
  selectedViewId?: number;
  onViewSelect: (view: SavedView) => void;
  onViewCreate: () => void;
  onViewEdit: (view: SavedView) => void;
  onViewDelete?: (view: SavedView) => void;
  className?: string;
  dropdownClassName?: string;
  handlerContainerClassName?: string;
  size?: ComponentSize;
  compactActions?: boolean;
}

export const SavedViewDropdown: React.FC<SavedViewDropdownProps> = memo(
  ({
    model,
    selectedViewId,
    onViewSelect,
    onViewCreate,
    onViewEdit,
    onViewDelete,
    className = '',
    dropdownClassName = '',
    handlerContainerClassName = '',
    size = 'md',
    compactActions = false,
  }) => {
    const { t } = useI18n();
    const { views, loading, error } = useSavedViews(model);
    const { user } = useCurrentUser();

    const handleViewSelect = useCallback(
      (view: SavedView) => {
        onViewSelect(view);
      },
      [onViewSelect]
    );

    const canUpdateView = useCallback(
      (view: SavedView) => {
        return !!user && view.userId === user.id;
      },
      [user]
    );

    const handleViewEdit = useCallback(
      (view: SavedView) => {
        if (!canUpdateView(view)) return;

        onViewEdit(view);
      },
      [canUpdateView, onViewEdit]
    );

    const handleViewDelete = useCallback(
      (view: SavedView) => {
        if (!onViewDelete) return;
        if (!canUpdateView(view)) return;

        onViewDelete(view);
      },
      [canUpdateView, onViewDelete]
    );

    const handleViewCreate = useCallback(() => {
      onViewCreate();
    }, [onViewCreate]);

    const selectedView = useMemo(
      () => views.find((view) => view.id === selectedViewId),
      [views, selectedViewId]
    );

    const userViews = useMemo(
      () => views.filter((view) => view.userId === user?.id && !view.isShared),
      [views, user?.id]
    );
    const sharedViews = useMemo(
      () => views.filter((view) => view.isShared),
      [views]
    );

    const getViewItemClasses = useCallback(
      (viewId: number) => {
        return viewId === selectedViewId
          ? 'text-primary-800 font-semibold'
          : 'text-secondary-800';
      },
      [selectedViewId]
    );

    const dropdownItems = useMemo(() => {
      const items: DropdownItem[] = [];

      items.push({
        content: 'Nueva vista',
        onClick: handleViewCreate,
        icon: (
          <PiPlus className="h-4 w-4 text-secondary-600 absolute right-4" />
        ),
      });

      if (selectedView && compactActions && canUpdateView(selectedView)) {
        items.push({
          content: 'Editar',
          onClick: () => handleViewEdit(selectedView),
          icon: (
            <PiPencilSimple className="h-4 w-4 text-secondary-600 absolute right-4" />
          ),
        });

        if (onViewDelete) {
          items.push({
            content: (
              <Typography className="font-medium text-danger-600">
                Eliminar
              </Typography>
            ),
            onClick: () => handleViewDelete(selectedView),
            icon: (
              <PiTrash className="h-4 w-4 text-danger-600 absolute right-4" />
            ),
          });
        }
      }

      if (selectedView) {
        items.push({
          separator: true,
          content: null,
          onClick: () => undefined,
        });
        items.push({
          content: 'Limpiar',
          onClick: () => handleViewSelect({ ...selectedView, id: -1 }),
          icon: (
            <PiXLight className="h-4 w-4 text-secondary-600 absolute right-4" />
          ),
          separator: true,
        });
      }

      if (userViews.length > 0) {
        userViews.forEach((view) => {
          items.push({
            content: (
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-1">
                  <Typography className={getViewItemClasses(view.id)}>
                    {view.name}
                  </Typography>
                </div>
              </div>
            ),
            onClick: () => handleViewSelect(view),
            selected: view.id === selectedViewId,
          });
        });
      }

      sharedViews.forEach((view) => {
        items.push({
          content: (
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-1.5">
                <PiUsers className={`h-4 w-4 ${getViewItemClasses(view.id)}`} />
                <Typography className={getViewItemClasses(view.id)}>
                  {view.name}
                </Typography>
              </div>
            </div>
          ),
          onClick: () => handleViewSelect(view),
          selected: view.id === selectedViewId,
        });
      });

      return items;
    }, [
      getViewItemClasses,
      handleViewSelect,
      handleViewCreate,
      selectedView,
      selectedViewId,
      sharedViews,
      userViews,
      compactActions,
      canUpdateView,
      handleViewEdit,
      handleViewDelete,
      onViewDelete,
    ]);

    const isViewShared = useMemo(() => selectedView?.isShared, [selectedView]);

    if (loading) {
      return (
        <div className={`flex items-center gap-2 ${className}`}>
          <Loading size="sm" />
        </div>
      );
    }

    if (error) {
      return (
        <ErrorState
          // TODO: check when implementing i18n
          title="Ocurrió un error"
          description="No se pudieron cargar las vistas"
          variant="danger"
          placement="horizontal"
          size="xs"
        />
      );
    }

    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div>
          <Tooltip
            content={selectedView?.description ?? null}
            size="sm"
            placement="bottom"
          >
            <div>
              <MenuDropdown
                label={selectedView ? selectedView.name : 'Elige una vista'}
                items={dropdownItems}
                mobileTitle={t(
                  'communications.dropdown.savedViews.mobileTitle'
                )}
                size={size}
                variant="secondary"
                appearance="outline"
                icon={isViewShared ? <PiUsers className="h-4 w-4" /> : null}
                disabled={loading}
                className={dropdownClassName}
                handlerContainerClassName={handlerContainerClassName}
              />
            </div>
          </Tooltip>
        </div>
      </div>
    );
  }
);

SavedViewDropdown.displayName = 'SavedViewDropdown';
